import React from 'react';
import {
  Building2,
  CreditCard,
  Wallet,
  Upload,
  FileText,
  AlertCircle,
  Loader,
  Loader2,
  Trash2,
  SaudiRiyal,
  X,
} from 'lucide-react';
import i18n from '../../../../../../i18n';
import swal from 'sweetalert';
import { getLoggedUser } from '../../../../../../state/user';
import { getPaymentMethods, deleteSavedCard, createIntent, confirmIntent } from '../../../../../../models/thawani';
import { createWallet } from '../../../../../../models/wallet';
import { walletPaymentSuccessUrl } from '../../../../../../constants/global';
import { createPayment } from '../../../../../home/pages/Enroll/services/enrollService';
import logoEdfaPay from '../../../../../../logoEdfaPay.svg';
import '../../../../../../styles/components/enroll-page.scss';

const user = getLoggedUser();

const MAX_FILE_SIZE_KB = 5120; // 5MB

export class RechargeForm extends React.Component {
  constructor() {
    super();
    this.state = {
      online_type: 'local',
      payment_method: 'offline',
      input: {
        recharge_amount: '',
        bank: '',
        account_number: '',
        receipt_number: '',
      },
      file_receipt: null,
      receiptPreviewUrl: null,
      cards: [],
      is_cards_loading: false,
      is_loading: false,
      is_payment_loading: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.onFileChange = this.onFileChange.bind(this);
    this.saveFund = this.saveFund.bind(this);
    this.gotoPayment = this.gotoPayment.bind(this);
    this.getCards = this.getCards.bind(this);
    this.deleteCard = this.deleteCard.bind(this);
    this.paymentUsingSavedCard = this.paymentUsingSavedCard.bind(this);
  }

  componentDidMount() {
    this.setState({ is_loading: false });
  }

  componentWillUnmount() {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
  }

  // ─── Handlers ──────────────────────────────────────────────

  handleChange(event) {
    let input = this.state.input;
    input[event.target.name] = event.target.value;
    this.setState({ input });
  }

  handlePaymentMethodChange(method) {
    this.setState({ payment_method: method });
    if (method === 'online') {
      this.getCards();
    }
  }

  onFileChange = (event) => {
    if (event.target.name !== 'file_receipt') return;
    const file = event.target.files[0];
    if (!file) return;

    if (file.size / 1024 > MAX_FILE_SIZE_KB) {
      swal('Failed', 'File size should be less than 5MB', 'error');
      return;
    }

    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
    this.setState({
      file_receipt: file,
      receiptPreviewUrl: URL.createObjectURL(file),
    });
  };

  removeReceiptFile = () => {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
    this.setState({ file_receipt: null, receiptPreviewUrl: null });
  };

  openReceiptPreview = () => {
    if (this.state.receiptPreviewUrl) {
      window.open(this.state.receiptPreviewUrl, '_blank');
    }
  };

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  // ─── Offline (Bank transfer) → createWallet ─────────────────

  async saveFund(e) {
    e.preventDefault();
    if (
      !this.state.input.recharge_amount ||
      !this.state.file_receipt ||
      !this.state.input.account_number ||
      !this.state.input.bank ||
      !this.state.input.receipt_number
    ) {
      swal(i18n.t('failed'), i18n.t('all_mandatory'), 'error');
      return;
    }

    this.setState({ is_loading: true });
    const formData = new FormData();
    formData.append('credit', this.state.input.recharge_amount);
    formData.append('account_number', this.state.input.account_number);
    formData.append('bank', this.state.input.bank);
    formData.append('receipt_number', this.state.input.receipt_number);
    formData.append('method', 'offline');
    formData.append('status', 'P');
    formData.append('type', 'wallet_recharge');
    formData.append('user', user.id);
    if (this.state.file_receipt) {
      formData.append('file_receipt', this.state.file_receipt, this.state.file_receipt.name);
    }

    const response = await createWallet(formData);
    if (response && response.success) {
      swal(i18n.t('success'), i18n.t('fund_added'), 'success');
      this.setState({ is_loading: false });
    } else {
      this.setState({ is_loading: false });
    }
  }

  // ─── Online (new payment) → EdfaPay ─────────────────────────

  async gotoPayment(e) {
    this.setState({ is_payment_loading: true });
    let recharge_amount = this.state.input.recharge_amount;
    let og_amount = this.state.input.recharge_amount;

    let { online_type } = this.state;
    let two_percent = (parseFloat(recharge_amount) * 2) / 100;
    let credit_amount = parseFloat(recharge_amount) + parseFloat(two_percent);

    let one_five_percent = (parseFloat(recharge_amount) * 1.5) / 100;
    let debit_amount = parseFloat(recharge_amount) + parseFloat(one_five_percent);
    if (online_type === 'local') {
      recharge_amount = debit_amount;
    }
    if (online_type === 'credit') {
      recharge_amount = credit_amount;
    }

    let reference_id = user.email;
    localStorage.setItem('client_reference_id', reference_id);

    const data = {
      amount: parseInt(recharge_amount),
      currency: 'SAR',
      description: 'Mzadcom Wallet : Recharge',
      type: 'wallet_recharge',
      og_amount: og_amount,
    };

    const response = await createPayment(data);
    window.location.href = response.data.redirect_url; // redirect to EdfaPay
    this.setState({ is_payment_loading: false });
  }

  // ─── Saved cards (Thawani) ──────────────────────────────────

  async getCards() {
    if (!user.thawani_id) {
      return;
    }
    this.setState({ is_cards_loading: true });
    const response = await getPaymentMethods(user.thawani_id);
    try {
      if (response.success) {
        this.setState({ is_cards_loading: false, cards: response.data });
      } else {
        this.setState({ is_cards_loading: false });
      }
    } catch (error) {
      this.setState({ is_cards_loading: false });
      console.error('Error fetching saved card:', error);
    }
  }

  async deleteCard(id) {
    swal({
      title: 'Confirm Deletion',
      text: 'Are you sure you want to delete this saved card?',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await deleteSavedCard(id);
          if (response.success) {
            swal('Success', 'Card deleted!!!', 'success');
            this.getCards();
          }
        } catch (error) {
          console.error('Error deleting saved card:', error);
        }
      }
    });
  }

  async paymentUsingSavedCard(id) {
    let { recharge_amount } = this.state.input;
    let { online_type } = this.state;
    let two_percent = (parseFloat(recharge_amount) * 2) / 100;
    let credit_amount = parseFloat(recharge_amount) + parseFloat(two_percent);

    let one_five_percent = (parseFloat(recharge_amount) * 1.5) / 100;
    let debit_amount = parseFloat(recharge_amount) + parseFloat(one_five_percent);
    if (online_type === 'local') {
      recharge_amount = debit_amount;
    }
    if (online_type === 'credit') {
      recharge_amount = credit_amount;
    }

    if (!id) {
      swal('Error', 'Please select a card', 'error');
      return;
    }
    if (!recharge_amount) {
      swal('Error', 'Please enter an amount before proceeding with saved card!!!', 'error');
      return;
    }

    let queryParams = '?ptype=online&type=wallet_recharge&card=1&amt=' + recharge_amount;
    let payload = {
      payment_method_id: id,
      amount: parseInt(recharge_amount) * 1000,
      client_reference_id: user.email,
      return_url: walletPaymentSuccessUrl + queryParams,
      metadata: {
        'Customer name': user.name,
        'Order id': user.id + '',
        user: user.id,
      },
    };
    const response = await createIntent(payload);
    if (response.success) {
      localStorage.setItem('payment_intent_id', response.data.id);
      localStorage.setItem('invoice', response.data.id);
      const resConf = await confirmIntent(response.data.id);
      if (resConf.success) {
        window.location.href = resConf.data.next_action.url;
      }
    }
  }

  handleSubmit = (e) => {
    if (!this.state.payment_method) {
      swal(i18n.t('failed'), i18n.t('choose_payment_method'), 'error');
      return;
    }
    if (!this.state.input.recharge_amount) {
      swal(i18n.t('failed'), 'Please enter an amount', 'error');
      return;
    }
    if (this.state.payment_method === 'offline') {
      this.saveFund(e);
    } else {
      this.gotoPayment(e);
    }
  };

  // ─── Renderers ─────────────────────────────────────────────

  renderAmountField() {
    const { input } = this.state;
    return (
      <div className="mze-field">
        <label className="mze-field__label">
          <SaudiRiyal size={14} />
          {i18n.t('enter_amount')}
          <span className="mze-field__required">*</span>
        </label>
        <input
          className="mze-field__input mze-field__input--amount"
          type="number"
          name="recharge_amount"
          placeholder="0.00"
          min="0"
          step="0.01"
          value={input.recharge_amount}
          onChange={this.handleChange}
          onWheel={(e) => e.target.blur()}
        />
      </div>
    );
  }

  renderPaymentTabs() {
    const { payment_method } = this.state;
    return (
      <div className="mze-tabs">
        <button
          className={`mze-tab ${payment_method === 'offline' ? 'mze-tab--active' : ''}`}
          onClick={() => this.handlePaymentMethodChange('offline')}
        >
          <Building2 size={16} />
          <span>{i18n.t('bank_transfer')}</span>
        </button>
        <button
          className={`mze-tab ${payment_method === 'online' ? 'mze-tab--active' : ''}`}
          onClick={() => this.handlePaymentMethodChange('online')}
        >
          <CreditCard size={16} />
          <span>{i18n.t('online')}</span>
        </button>
      </div>
    );
  }

  renderPolicy() {
    return (
      <div className="mze-policy">
        <strong className="mze-policy__title">{i18n.t('online_deposit_and_refund_policy')}</strong>
        <div className="mze-policy__text">
          <p>1) Those willing to participate in the e-auction must deposit a Guarantee amount.</p>
          <p>2) The Guarantee amount is refundable. However, the Service Charges for the Online Payment is not refundable, which is 1.5% for local cards and 2% for credit cards.</p>
          <p>3) The guarantee amount is separate from the value of the items offered in the auction.</p>
          <p>4) The Guarantee amount may vary from an item to another, and it may be a fixed amount or a percentage of the present value of the offered item.</p>
          <p>5) The Guarantee amount is not automatically returned. In case of delay, Mzadcom will not bear any delay in refund.</p>
          <p>6) The Guarantee amount will return to the Bidder's wallet and Bidders can request for refund.</p>
        </div>
      </div>
    );
  }

  renderUploadZone() {
    const { file_receipt, receiptPreviewUrl } = this.state;
    const isPdf = file_receipt?.type === 'application/pdf';
    const isImage = file_receipt?.type?.startsWith('image/');

    return (
      <div className="mze-field mze-field--upload">
        <label className="mze-field__label">
          <Upload size={14} />
          {i18n.t('receipt')}
          <span className="mze-field__required">*</span>
        </label>

        <input
          type="file"
          id="wallet_file_receipt"
          name="file_receipt"
          accept=".jpg,.jpeg,.png,.pdf"
          style={{ display: 'none' }}
          onChange={this.onFileChange}
        />

        {!file_receipt ? (
          <div className="mze-upload-zone">
            <div className="mze-upload-zone__icon">
              <Upload size={24} />
            </div>
            <p className="mze-upload-zone__title">
              {i18n.t('drop_receipt_here') || 'Upload your receipt'}
            </p>
            <p className="mze-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
            <button
              type="button"
              className="mze-upload-zone__btn"
              onClick={() => document.getElementById('wallet_file_receipt').click()}
            >
              {i18n.t('browse_file') || 'Browse file'}
            </button>
          </div>
        ) : (
          <div className="mze-receipt-preview">
            <div className="mze-receipt-preview__header">
              <FileText size={15} className="mze-receipt-preview__file-icon" />
              <span className="mze-receipt-preview__name">{file_receipt.name}</span>
              <span className="mze-receipt-preview__size">
                {this.formatFileSize(file_receipt.size)}
              </span>
              <button
                type="button"
                className="mze-receipt-preview__remove"
                onClick={this.removeReceiptFile}
              >
                <X size={12} /> {i18n.t('remove') || 'Remove'}
              </button>
            </div>

            <div className={`mze-receipt-preview__body ${isImage ? 'mze-receipt-preview__body--image' : ''}`}>
              {isImage && (
                <img
                  src={receiptPreviewUrl}
                  alt="Receipt preview"
                  className="mze-receipt-preview__img"
                />
              )}
              {isPdf && (
                <div className="mze-receipt-preview__pdf">
                  <FileText size={32} className="mze-receipt-preview__pdf-icon" />
                  <div>
                    <p className="mze-receipt-preview__pdf-name">{file_receipt.name}</p>
                    <span className="mze-receipt-preview__pdf-meta">
                      PDF document · {this.formatFileSize(file_receipt.size)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mze-receipt-preview__actions">
              <button
                type="button"
                className="mze-receipt-preview__view-btn"
                onClick={this.openReceiptPreview}
              >
                <FileText size={13} /> {i18n.t('view_full_size') || 'View full size'}
              </button>
              <button
                type="button"
                className="mze-receipt-preview__change-btn"
                onClick={() => document.getElementById('wallet_file_receipt').click()}
              >
                <Upload size={13} /> {i18n.t('change_file') || 'Change file'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderOfflineForm() {
    const { input } = this.state;
    const fields = [
      { name: 'bank', label: i18n.t('bank'), placeholder: i18n.t('please_enter_bank_name') },
      {
        name: 'account_number',
        label: i18n.t('account_number'),
        placeholder: i18n.t('please_enter_bank_account'),
      },
      {
        name: 'receipt_number',
        label: i18n.t('receipt'),
        placeholder: i18n.t('please_enter_receipt_number'),
        full: true,
      },
    ];

    return (
      <div className="mze-form-block">
        {this.renderPolicy()}
        {this.renderUploadZone()}

        <div className="mze-offline-fields">
          {fields.map((field) => (
            <div className={`mze-field ${field.full ? 'mze-field--full' : ''}`} key={field.name}>
              <label className="mze-field__label">
                <span className="mze-field__required">*</span>
                {field.label}
              </label>
              <input
                className="mze-field__input"
                type="text"
                name={field.name}
                placeholder={field.placeholder}
                value={input[field.name] || ''}
                onChange={this.handleChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderSavedCards() {
    const { cards, is_cards_loading } = this.state;

    if (is_cards_loading) {
      return (
        <div className="mze-cards-loading">
          <Loader2 size={22} className="mze-submit__spinner" />
        </div>
      );
    }

    if (!cards || cards.length === 0) {
      return (
        <div className="mze-alert mze-alert--compact" style={{ marginTop: '0.75rem' }}>
          <AlertCircle size={16} className="mze-alert__icon" />
          <span>{i18n.t('no_saved_cards')}</span>
        </div>
      );
    }

    return (
      <div className="mze-saved-cards">
        <h6 className="mze-saved-cards__title">{i18n.t('saved_cards')}</h6>
        <div className="mze-saved-cards__grid">
          {cards.map((item) => (
            <div className="mze-saved-card" key={item.id}>
              <button
                type="button"
                className="mze-saved-card__delete"
                onClick={() => this.deleteCard(item.id)}
              >
                <Trash2 size={13} />
              </button>
              <div
                className="mze-saved-card__body"
                onClick={() => this.paymentUsingSavedCard(item.id)}
              >
                <CreditCard size={22} className="mze-saved-card__brand" />
                <div className="mze-saved-card__number">{item.masked_card}</div>
                <div className="mze-saved-card__meta">
                  {i18n.t('name')} : {item.nickname}
                </div>
                <div className="mze-saved-card__meta">
                  {i18n.t('expiry_month_year')} {item.expiry_month}/{item.expiry_year}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderOnlineForm() {
    const { online_type, input } = this.state;
    const amount = parseFloat(input.recharge_amount) || 0;
    const debit_amount = amount + (amount * 1.5) / 100;
    const credit_amount = amount + (amount * 2) / 100;

    return (
      <div className="mze-form-block">
        {this.renderPolicy()}

        <div className="mze-thawani">
          <img src={logoEdfaPay} alt="EdfaPay" className="mze-thawani__logo" height={30} />
          <p className="mze-thawani__text">{i18n.t('redirect_to_payment')}</p>
          <div className="mze-card-types">
            <button
              className={`mze-card-type ${online_type === 'local' ? 'mze-card-type--active' : ''}`}
              onClick={() => this.setState({ online_type: 'local' })}
            >
              <div>{i18n.t('local_cards_ecommended')}</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                {debit_amount.toFixed(2)} <SaudiRiyal size={13} />
              </div>
            </button>
            <button
              className={`mze-card-type ${online_type === 'credit' ? 'mze-card-type--active' : ''}`}
              onClick={() => this.setState({ online_type: 'credit' })}
            >
              <div>{i18n.t('credit_cards_coast_2')}</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                {credit_amount.toFixed(2)} <SaudiRiyal size={13} />
              </div>
            </button>
          </div>
        </div>

        {this.renderSavedCards()}
      </div>
    );
  }

  renderSubmitButton() {
    const { is_loading, is_payment_loading } = this.state;
    const isBusy = is_loading || is_payment_loading;

    return (
      <button type="button" className="mze-submit" disabled={isBusy} onClick={this.handleSubmit}>
        {isBusy ? (
          <>
            <Loader size={16} className="mze-submit__spinner" /> {i18n.t('saving')}
          </>
        ) : (
          i18n.t('submit')
        )}
      </button>
    );
  }

  render() {
    const { payment_method } = this.state;

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Wallet size={16} />
          <span>{i18n.t('add_money_wallet')}</span>
        </div>

        <div className="mze-panel__body">
          {this.renderAmountField()}
          {this.renderPaymentTabs()}

          {payment_method === 'offline' && this.renderOfflineForm()}
          {payment_method === 'online' && this.renderOnlineForm()}

          <div className="mze-submit-area">{this.renderSubmitButton()}</div>
        </div>
      </div>
    );
  }
}